/**
 * Date formatting utilities for local timezone display
 */

/**
 * Formats date part only (MMM dd format like "Nov 18")
 * @param dateString - ISO date string or Date object
 * @returns Formatted date string in MMM dd format
 */
export const formatDateOnly = (dateString: string | Date | undefined): string => {
  if (!dateString || dateString === 'N/A') return 'N/A';
  
  try {
    const date = new Date(dateString);
    
    // Check if date is valid
    if (isNaN(date.getTime())) return 'Invalid Date';
    
    // Format date in user's local timezone
    const options: Intl.DateTimeFormatOptions = {
      month: 'short',
      day: '2-digit',
      timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone
    };
    
    return new Intl.DateTimeFormat('en-US', options).format(date);
    
  } catch (error) {
    console.error('Error formatting date:', error);
    return 'Invalid Date';
  }
};

/**
 * Formats time part only (HH:mm:ss format)
 * @param dateString - ISO date string or Date object
 * @returns Formatted time string in HH:mm:ss format
 */
export const formatTimeOnly = (dateString: string | Date | undefined): string => {
  if (!dateString || dateString === 'N/A') return 'N/A';
  
  try {
    const date = new Date(dateString);
    
    // Check if date is valid
    if (isNaN(date.getTime())) return 'Invalid Date';
    
    // Format time in user's local timezone
    const options: Intl.DateTimeFormatOptions = {
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: false,
      timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone
    };
    
    return new Intl.DateTimeFormat('en-US', options).format(date);
    
  } catch (error) {
    console.error('Error formatting time:', error);
    return 'Invalid Date';
  }
};

/**
 * Formats a date string to the user's local timezone (for backward compatibility)
 * @param dateString - ISO date string or Date object
 * @returns Formatted date string in local timezone with timezone name
 */
export const formatLocalDateTime = (dateString: string | Date | undefined): string => {
  if (!dateString || dateString === 'N/A') return 'N/A';
  
  try {
    const date = new Date(dateString);
    
    // Check if date is valid
    if (isNaN(date.getTime())) return 'Invalid Date';
    
    // Format date in user's local timezone
    const options: Intl.DateTimeFormatOptions = {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      timeZoneName: 'short',
      hour12: false // Use 24-hour format
    };
    
    const formatter = new Intl.DateTimeFormat('en-US', options);
    
    // Reformat to desired format: YYYY-MM-DD HH:mm:ss TZ
    const parts = formatter.formatToParts(date);
    const partsMap: { [key: string]: string } = {};
    
    parts.forEach(part => {
      partsMap[part.type] = part.value;
    });
    
    return `${partsMap.year}-${partsMap.month}-${partsMap.day} ${partsMap.hour}:${partsMap.minute}:${partsMap.second} ${partsMap.timeZoneName}`;
    
  } catch (error) {
    console.error('Error formatting date:', error);
    return 'Invalid Date';
  }
};

/**
 * Formats a date string to a more user-friendly local time format
 * @param dateString - ISO date string or Date object
 * @returns Formatted date string with relative time if recent
 */
export const formatLocalDateTimeWithRelative = (dateString: string | Date | undefined): string => {
  if (!dateString || dateString === 'N/A') return 'N/A';
  
  try {
    const date = new Date(dateString);
    
    // Check if date is valid
    if (isNaN(date.getTime())) return 'Invalid Date';
    
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
    
    // If less than 24 hours ago, show relative time
    if (diffHours < 24 && diffHours >= 0) {
      if (diffHours === 0) {
        const diffMinutes = Math.floor(diffMs / (1000 * 60));
        if (diffMinutes < 1) return 'Just now';
        if (diffMinutes === 1) return '1 minute ago';
        if (diffMinutes < 60) return `${diffMinutes} minutes ago`;
      }
      if (diffHours === 1) return '1 hour ago';
      return `${diffHours} hours ago`;
    }
    
    // Otherwise, show full local datetime
    return formatLocalDateTime(dateString);
    
  } catch (error) {
    console.error('Error formatting date:', error);
    return 'Invalid Date';
  }
};

/**
 * Gets the user's timezone abbreviation
 * @returns Timezone abbreviation (e.g., "EST", "PST", "GMT")
 */
export const getUserTimezone = (): string => {
  try {
    const formatter = new Intl.DateTimeFormat('en-US', { timeZoneName: 'short' });
    const parts = formatter.formatToParts(new Date());
    const timeZoneName = parts.find(part => part.type === 'timeZoneName');
    return timeZoneName?.value || 'Local';
  } catch (error) {
    console.error('Error getting timezone:', error);
    return 'Local';
  }
};

/**
 * Converts UTC date string to local ISO format
 * @param utcDateString - UTC date string
 * @returns ISO string in local timezone
 */
export const utcToLocalISO = (utcDateString: string | Date | undefined): string => {
  if (!utcDateString || utcDateString === 'N/A') return 'N/A';
  
  try {
    const date = new Date(utcDateString);
    if (isNaN(date.getTime())) return 'Invalid Date';
    
    // Return ISO string (which will be in UTC but displayed in local when formatted)
    return date.toISOString();
  } catch (error) {
    console.error('Error converting UTC to local:', error);
    return 'Invalid Date';
  }
};